/**
 * The commune-admin console's own shell (`docs/ADMIN.md` §2): a violet
 * 210px left nav, content on the right, `overflow-x: auto`. A second
 * layout, deliberately not `AppShell` — no bottom nav, no session banner —
 * because a coordinator preparing a canvassing week is not mid-session.
 * Below 900px the nav collapses to a horizontal strip above the content.
 */
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext';
import { t } from '../../i18n/fr';

import './admin.css';

/**
 * Render the console shell: the left/top nav and the routed page in
 * `<Outlet/>`.
 *
 * @returns The shell element.
 */
export function AdminLayout() {
  const { insee } = useParams<{ insee: string }>();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const base = `/admin/${insee}`;
  const items = [
    { to: base, label: t('admin.nav.commune'), end: true },
    { to: `${base}/inscriptions`, label: t('admin.nav.inscriptions'), end: false },
    { to: `${base}/notes`, label: t('admin.nav.notes'), end: false },
    { to: `${base}/engagements`, label: t('admin.nav.engagements'), end: false },
  ];

  return (
    <div className="admin">
      <nav className="admin__nav" aria-label={t('admin.nav.ariaLabel')}>
        <p className="admin__brand">{t('app.name')}</p>
        <ul className="admin__nav-list">
          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  isActive ? 'admin__nav-link admin__nav-link--active' : 'admin__nav-link'
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="admin__nav-footer">
          <button
            type="button"
            className="admin__nav-exit"
            onClick={() => {
              navigate('/');
            }}
          >
            {t('admin.nav.backToApp')}
          </button>
          <button
            type="button"
            className="admin__nav-logout"
            onClick={() => {
              void logout();
            }}
          >
            {t('userMenu.logout')}
          </button>
        </div>
      </nav>
      <div className="admin__content">
        <Outlet />
      </div>
    </div>
  );
}
